import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import { usePagedData } from '../../hooks/usePagedData';
import { purchaseOrdersApi } from '../../api/endpoints';
import { PurchaseOrderList, PurchaseOrderStatusMap, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiCheckCircle, FiDownload, FiX, FiFileText, FiPackage, FiTruck, FiClock, FiDollarSign, FiAlertCircle, FiShoppingBag, FiUploadCloud } from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import { toast } from 'react-toastify';
import CountryFlag from '../../components/common/CountryFlag';
import SapSyncBadge from '../../components/common/SapSyncBadge';
import ConfirmSapSyncDialog, { SapSyncOrderRow } from '../../components/common/ConfirmSapSyncDialog';
import { SapSyncStatus } from '../../types';

/* ------------------------------------------------------------------ */
/*  Marketplace name → ISO country code                                */
/* ------------------------------------------------------------------ */

const marketplaceToCountry: Record<string, string> = {
  'amazon.de': 'DE', 'amazon.fr': 'FR', 'amazon.it': 'IT', 'amazon.es': 'ES',
  'amazon.co.uk': 'GB', 'amazon.com': 'US', 'amazon.pl': 'PL', 'amazon.se': 'SE',
  'amazon.nl': 'NL', 'amazon.com.be': 'BE', 'amazon.co.jp': 'JP', 'amazon.ca': 'CA',
  'amazon.com.au': 'AU', 'amazon.com.mx': 'MX', 'amazon.in': 'IN', 'amazon.com.br': 'BR',
  'amazon.com.tr': 'TR', 'amazon.ae': 'AE', 'amazon.sa': 'SA', 'amazon.sg': 'SG',
};

const getMarketplaceCountry = (marketplace: string | null): string | null => {
  if (!marketplace) return null;
  const key = marketplace.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return marketplaceToCountry[key] || null;
};

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */

const statusConfig: Record<number, { color: string; bg: string; style: 'dot' | 'filled' | 'outline' | 'strikethrough' }> = {
  0: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)', style: 'dot' },
  1: { color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.12)', style: 'dot' },
  2: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', style: 'dot' },
  3: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', style: 'dot' },
  4: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', style: 'dot' },
  5: { color: '#fff', bg: 'rgba(16, 185, 129, 0.85)', style: 'filled' },
  6: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', style: 'dot' },
  7: { color: '#10b981', bg: 'transparent', style: 'outline' },
  8: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', style: 'strikethrough' },
  9: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)', style: 'dot' },
};

const StatusDotBadge: React.FC<{ status: number }> = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig[0];
  const label = PurchaseOrderStatusMap[status] || `Status ${status}`;

  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
    whiteSpace: 'nowrap',
  };

  if (cfg.style === 'filled') {
    return (
      <span style={{ ...base, fontWeight: 600, color: cfg.color, backgroundColor: cfg.bg }}>
        {label}
      </span>
    );
  }
  if (cfg.style === 'outline') {
    return (
      <span style={{ ...base, fontWeight: 600, color: cfg.color, backgroundColor: 'transparent', border: `1.5px solid ${cfg.color}` }}>
        {label}
      </span>
    );
  }
  if (cfg.style === 'strikethrough') {
    return (
      <span style={{ ...base, fontWeight: 500, color: cfg.color, backgroundColor: cfg.bg, textDecoration: 'line-through' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cfg.color, display: 'inline-block', flexShrink: 0 }} />
        {label}
      </span>
    );
  }
  // dot (default)
  return (
    <span style={{ ...base, fontWeight: 500, color: cfg.color, backgroundColor: cfg.bg }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cfg.color, display: 'inline-block', flexShrink: 0 }} />
      {label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Date helper                                                        */
/* ------------------------------------------------------------------ */

const formatRelativeDate = (dateStr: string, t: any): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('orders.dateToday');
  if (diffDays === 1) return t('orders.dateYesterday');
  if (diffDays <= 6) return t('orders.dateDaysAgo', { count: diffDays });

  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/* ------------------------------------------------------------------ */
/*  Summary stat card component                                        */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value }) => (
  <div className="po-stat-card" style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
        {value}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Summary shape                                                      */
/* ------------------------------------------------------------------ */

interface SummaryData {
  totalOrders: number;
  totalValue: number;
  currency: string;
  shippedCount: number;
  pendingCount: number;
  cancelledCount: number;
  withItemsCount: number;
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const PurchaseOrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<SummaryData | null>(null);
  // SAP-sync confirmation dialog
  const [sapDialogOpen, setSapDialogOpen] = useState(false);
  const [sapSubmitting, setSapSubmitting] = useState(false);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<PurchaseOrderList>(purchaseOrdersApi.list, {
    initialSortBy: 'orderDate',
    initialSortDesc: true,
  });

  /* Fetch summary stats from backend (across ALL pages) */
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const resp = await purchaseOrdersApi.summary(filters);
        if (!cancelled) setSummary(resp.data);
      } catch {
        // Silently fail - stats are non-critical
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, [filters]);

  /* ---- Handlers ---- */

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback(
    (value: string) => {
      setDateFrom(value);
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        dateFrom: value || undefined,
      }));
    },
    [setFilters]
  );

  const handleDateToChange = useCallback(
    (value: string) => {
      setDateTo(value);
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        dateTo: value || undefined,
      }));
    },
    [setFilters]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await purchaseOrdersApi.sync();
      toast.success(t('orders.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  const handleRowClick = useCallback(
    (row: PurchaseOrderList) => {
      navigate(`/purchase-orders/${row.id}`);
    },
    [navigate]
  );

  const handleBulkConfirm = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    setBulkProgress({ current: 0, total: selectedIds.size });
    try {
      const result = await purchaseOrdersApi.bulkConfirm(Array.from(selectedIds));
      const r = result.data;
      setBulkProgress({ current: r.total, total: r.total });
      toast.success(t('orders.bulkConfirmSuccess', { success: r.success, total: r.total }));
      if (r.success < r.total) {
        const errors = r.results.filter((x: any) => x.status === 'error');
        errors.forEach((e: any) => toast.error(`PO ${e.id.substring(0, 8)}: ${e.message}`));
      }
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.bulkConfirmError'));
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedIds, refresh, t]);

  /* ---- SAP sync queue ---- */

  // Resolve the selected IDs to full row objects so the dialog can render them
  // (PO#, channel, marketplace, status, amount). We only consider rows that are
  // actually loaded on the current page -- if the user paginated and selected
  // rows on previous pages (no current support but defensive) those rows are
  // missing the metadata, so we skip them.
  const sapDialogOrders = useMemo<SapSyncOrderRow[]>(() => {
    const items = data?.items ?? [];
    return items
      .filter(o => selectedIds.has(o.id))
      .map(o => ({
        id: o.id,
        amazonPurchaseOrderNumber: o.amazonPurchaseOrderNumber,
        status: o.status,
        channel: o.channel,
        marketplace: o.marketplace,
        shipToCountry: o.shipToCountry,
        totalAmount: o.totalAmount,
        currency: o.currency,
      }));
  }, [data?.items, selectedIds]);

  const handleOpenSapDialog = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.info(t('orders.sapSyncSelectFirst'));
      return;
    }
    setSapDialogOpen(true);
  }, [selectedIds.size, t]);

  const handleConfirmSapSync = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setSapSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const response = await purchaseOrdersApi.queueSapSync(ids);
      const queued = response.data?.queued ?? 0;
      const requested = response.data?.requested ?? ids.length;
      const skipped = response.data?.skipped ?? 0;
      toast.success(t('orders.sapSyncSuccess', { queued, requested }));
      if (skipped > 0) {
        toast.info(t('orders.sapSyncSkipped', { count: skipped }));
      }
      setSapDialogOpen(false);
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.sapSyncError'));
    } finally {
      setSapSubmitting(false);
    }
  }, [selectedIds, refresh, t]);

  const handleExportCsv = useCallback(async () => {
    try {
      const response = await purchaseOrdersApi.exportCsv(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('orders.csvExported'));
    } catch (error: any) {
      toast.error(t('orders.csvExportError'));
    }
  }, [filters, t]);

  /* ---- Formatting ---- */

  const formatCurrency = (amount: number, currency?: string) => {
    const cur = currency || 'EUR';
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
  };

  /* ---- Columns ---- */

  const columns = useMemo(() => [
    {
      key: 'amazonPurchaseOrderNumber',
      header: t('orders.poNumber'),
      sortable: true,
      render: (row: PurchaseOrderList) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <FiFileText style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: '14px' }} />
          <span style={{ fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", fontWeight: 700, fontSize: '13px' }}>
            {row.amazonPurchaseOrderNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'marketplace',
      header: t('orders.marketplace'),
      className: 'col-hide-mobile',
      render: (row: PurchaseOrderList) => (
        <span title={row.marketplace || ''} style={{ display: 'inline-flex', alignItems: 'center' }}>
          <CountryFlag countryCode={getMarketplaceCountry(row.marketplace)} size={16} />
        </span>
      ),
    },
    {
      key: 'shipToCountry',
      header: t('orders.country'),
      className: 'col-hide-mobile',
      render: (row: PurchaseOrderList) => {
        if (!row.shipToCountry) return <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>;
        return (
          <span title={row.shipToCountry} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
            <CountryFlag countryCode={row.shipToCountry} size={14} />
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{row.shipToCountry}</span>
          </span>
        );
      },
    },
    {
      key: 'channel',
      header: t('orders.channel'),
      className: 'col-hide-mobile',
      render: (row: PurchaseOrderList) => {
        const isVendor = row.channel === 1;
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
            color: isVendor ? '#7c3aed' : '#3b82f6',
            backgroundColor: isVendor ? 'rgba(124, 58, 237, 0.12)' : 'rgba(59, 130, 246, 0.12)',
          }}>
            {ChannelMap[row.channel] || `Channel ${row.channel}`}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (row: PurchaseOrderList) => <StatusDotBadge status={row.status} />,
    },
    {
      key: 'sapSyncStatus',
      header: t('orders.sapSyncColumn'),
      sortable: true,
      render: (row: PurchaseOrderList) => <SapSyncBadge status={row.sapSyncStatus} />,
    },
    {
      key: 'orderDate',
      header: t('orders.orderDate'),
      sortable: true,
      render: (row: PurchaseOrderList) => (
        <span title={new Date(row.orderDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}>
          {formatRelativeDate(row.orderDate, t)}
        </span>
      ),
    },
    {
      key: 'totalAmount',
      header: t('orders.amount'),
      sortable: true,
      render: (row: PurchaseOrderList) => {
        if (row.totalAmount === 0) {
          return (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '12px' }}>
              <FiClock style={{ marginRight: '4px', verticalAlign: 'middle', fontSize: '12px' }} />
              {t('orders.amountPending')}
            </span>
          );
        }
        return (
          <span style={{ fontWeight: 600, color: '#10b981' }}>
            {formatCurrency(row.totalAmount, row.currency)}
          </span>
        );
      },
    },
    {
      key: 'itemCount',
      header: t('orders.items'),
      render: (row: PurchaseOrderList) => {
        if (row.itemCount === 0) {
          return <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>;
        }
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: '26px', height: '22px', padding: '0 8px',
            borderRadius: '11px', fontSize: '12px', fontWeight: 600,
            backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
          }}>
            {row.itemCount}
          </span>
        );
      },
    },
    {
      key: 'firstItemTitle',
      header: t('orders.firstItem'),
      className: 'col-hide-tablet',
      render: (row: PurchaseOrderList) => {
        if (!row.firstItemTitle) {
          return <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>;
        }
        const truncated = row.firstItemTitle.length > 30
          ? row.firstItemTitle.substring(0, 30) + '...'
          : row.firstItemTitle;
        return (
          <span title={row.firstItemTitle} style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {truncated}
          </span>
        );
      },
    },
  ], [t]);

  /* ---- Render ---- */

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('orders.title')}</h1>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleExportCsv}>
            <FiDownload /> CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.purchaseOrdersXlsx, `zamowienia_${new Date().toISOString().slice(0, 10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          {/* SAP sync button -- disabled when no selection, enabled with count badge once
              the user ticks rows. Same pattern as Gmail's archive/delete buttons. */}
          <button
            type="button"
            className="btn"
            onClick={handleOpenSapDialog}
            disabled={selectedIds.size === 0 || sapSubmitting}
            title={selectedIds.size === 0 ? t('orders.sapSyncSelectFirst') : undefined}
            style={{
              backgroundColor: selectedIds.size === 0 ? 'var(--bg-elevated)' : '#3b82f6',
              borderColor: selectedIds.size === 0 ? 'var(--border)' : '#3b82f6',
              color: selectedIds.size === 0 ? 'var(--text-muted)' : '#fff',
              fontWeight: 600,
              cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
              opacity: selectedIds.size === 0 ? 0.7 : 1,
              transition: 'all 150ms ease',
            }}
          >
            <FiUploadCloud />
            {t('orders.sapSyncButton')}
            {selectedIds.size > 0 && (
              <span style={{
                marginLeft: '6px',
                padding: '1px 7px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                fontSize: '11px',
                fontWeight: 700,
              }}>
                {selectedIds.size}
              </span>
            )}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('orders.syncing') : t('orders.syncWithAmazon')}
          </button>
        </div>
      </div>

      <ConfirmSapSyncDialog
        isOpen={sapDialogOpen}
        loading={sapSubmitting}
        orders={sapDialogOrders}
        statusLabel={(s) => PurchaseOrderStatusMap[s] || `Status ${s}`}
        onCancel={() => { if (!sapSubmitting) setSapDialogOpen(false); }}
        onConfirm={handleConfirmSapSync}
      />

      {/* Summary stats cards - data from backend API across ALL pages */}
      {summary && (
        <div className="po-stats-grid">
          <StatCard
            icon={<FiPackage />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('orders.statTotalOrders')}
            value={summary.totalOrders}
          />
          <StatCard
            icon={<FiDollarSign />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('orders.statTotalValue')}
            value={formatCurrency(summary.totalValue, summary.currency)}
          />
          <StatCard
            icon={<FiTruck />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('orders.statShipped')}
            value={summary.shippedCount}
          />
          <StatCard
            icon={<FiClock />}
            iconBg="rgba(245, 158, 11, 0.15)"
            iconColor="#f59e0b"
            label={t('orders.statPending')}
            value={summary.pendingCount}
          />
          <StatCard
            icon={<FiAlertCircle />}
            iconBg="rgba(239, 68, 68, 0.15)"
            iconColor="#ef4444"
            label={t('orders.statCancelled')}
            value={summary.cancelledCount}
          />
          <StatCard
            icon={<FiShoppingBag />}
            iconBg="rgba(124, 58, 237, 0.15)"
            iconColor="#7c3aed"
            label={t('orders.statWithItems')}
            value={summary.withItemsCount}
          />
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span style={{ fontWeight: 600 }}>
            {t('orders.selectedCount', { count: selectedIds.size })}
          </span>
          {bulkProcessing && (
            <div style={{ flex: 1, maxWidth: '200px' }}>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
                />
              </div>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                {bulkProgress.current}/{bulkProgress.total}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleBulkConfirm}
              disabled={bulkProcessing}
            >
              <FiCheckCircle /> {bulkProcessing ? t('orders.confirming') : t('orders.confirmSelected')}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkProcessing}
            >
              <FiX /> {t('orders.deselect')}
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('orders.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              ...Object.entries(PurchaseOrderStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder={t('common.allStatuses')}
          />

          <Select
            options={[
              { value: '', label: t('orders.allChannels') },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder={t('orders.allChannels')}
          />
        </div>
      </div>

      {/* Date range filter with quick buttons */}
      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={9} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
